/**
 * useModuleAccess Hook
 * Checks module access based on tenant.modules for tenant_owner users
 */

import { useSelector } from 'react-redux';
import { RootState } from '@/store';
import { useAuth } from './useAuth';

// Single source of truth for "every module key that can appear in
// tenant.modules" on this app — getAccessibleModules/getRestrictedModules
// below used to each hand-maintain their own separate copy of this list
// (already duplicated within this file), and it had drifted from the
// backend's canonical list (MODULE_REGISTRY in
// backend/src/utils/module-defaults.util.ts), silently omitting instagram,
// llm, automation, integrations, leadForm, verticals, leadScoring, and
// billing.usageHistory from any "all your modules" summary. Keep this in
// sync with that backend registry by hand — there is no shared-types package
// between the two apps to enforce it automatically.
const ALL_MODULE_KEYS: string[] = [
  'playground',
  'activity.chatLogs',
  'activity.leads',
  'activity.media',
  'analytics.chats',
  'analytics.topics',
  'analytics.sentiment',
  'sources.files',
  'sources.text',
  'sources.website',
  'sources.qa',
  'sources.notion',
  'sources.suggestions',
  'sources.media',
  'agents',
  'contacts',
  'appointments',
  'campaigns',
  'instagram',
  'instagramComments',
  'whatsapp',
  'llm',
  'automation',
  'integrations',
  'leadForm',
  'verticals',
  'leadScoring',
  'billing.amount',
  'billing.ai',
  'billing.activePlan',
  'billing.usageHistory',
  'userManagement',
  'settings',
  'systemAlerts',
  'privacyPolicy',
  'reports',
];

// Map routes to nested module paths
export const routeToModuleMap: Record<string, string> = {
  '/dashboard': 'dashboard',
  '/playground': 'playground',
  '/activity/chat-logs': 'activity.chatLogs',
  '/activity/leads': 'activity.leads',
  '/activity/media': 'activity.media',
  // Instagram Comments (Phase 3) — gated on its OWN master-side module
  // `instagramComments` (plan default mirrors `instagram`, super-admin can
  // revoke per tenant). The nav additionally hides it until the account is
  // connected (AdminLayout), and connection itself requires the `instagram`
  // module — so the item shows only when BOTH modules are on AND connected.
  '/activity/instagram-comments': 'instagramComments',
  // Captured public-webchat-widget session transcripts — same underlying
  // 'Web Chats' feature as /web-chats below (that's the settings page,
  // this is the transcript viewer), so both map to 'leadForm'.
  '/activity/web-captures': 'activity.chatLogs',
  '/activity/integrations': 'integrations',
  '/analytics/chats': 'analytics.chats',
  '/analytics/topics': 'analytics.topics',
  '/analytics/sentiment': 'analytics.sentiment',
  '/sources/files': 'sources.files',
  '/sources/text': 'sources.text',
  '/sources/website': 'sources.website',
  '/sources/qa': 'sources.qa',
  '/sources/notion': 'sources.notion',
  '/sources/suggestions': 'sources.suggestions',
  // Media Library is now the top-level /media route; the legacy /sources/media
  // path is kept here too so its redirect still resolves to the same module.
  '/media': 'sources.media',
  '/sources/media': 'sources.media',
  '/agents': 'agents',
  '/whatsapp-templates': 'whatsapp',
  '/whatsapp-interactive-messages': 'whatsapp',
  '/contacts': 'contacts',
  '/appointments': 'appointments',
  '/campaigns': 'campaigns',
  // Actual sidebar path includes the /workflows suffix — this map does an
  // exact match on the full path, not a prefix match, so '/automation'
  // alone would never have matched the real route.
  '/automation/workflows': 'automation',
  '/web-chats': 'leadForm',
  // All four billing pages gate on the single billing.activePlan key — the
  // backend collapses every /billing route to requirePermission('billing.activePlan')
  // and the per-page keys (billing.amount/ai/usageHistory) are hidden from the
  // grant surface, so mapping to them would blank these menu items for every
  // role that isn't implicit-ALL_ACCESS.
  '/billing/amount': 'billing.activePlan',
  '/billing/ai': 'billing.activePlan',
  '/billing/active-plan': 'billing.activePlan',
  '/billing/usage-history': 'billing.activePlan',
  '/billing/wallet': 'billing.activePlan',
  '/reports': 'reports',
  '/user-management': 'userManagement',
  '/settings': 'settings',
  '/system-alerts': 'systemAlerts',
};

// Map menu items to nested module paths
export const menuItemToModuleMap: Record<string, string> = {
  Playground: 'playground',
  Activity: 'activity',
  'Chat Logs': 'activity.chatLogs',
  Leads: 'activity.leads',
  // Distinct from the 'Media' label under Sources (the sendable library) —
  // menuItemToModuleMap is keyed by LABEL, so two identical keys would silently
  // collapse and gate this item on the wrong module.
  'Inbound Media': 'activity.media',
  Analytics: 'analytics',
  Chats: 'analytics.chats',
  Topics: 'analytics.topics',
  Sentiment: 'analytics.sentiment',
  Sources: 'sources',
  Files: 'sources.files',
  Text: 'sources.text',
  Website: 'sources.website',
  'Q&A': 'sources.qa',
  Notion: 'sources.notion',
  Suggestions: 'sources.suggestions',
  Media: 'sources.media',
  Reports: 'reports',
  Agents: 'agents',
  // Sidebar entry was renamed 'WhatsApp Templates' -> 'WhatsApp' (the page it
  // opens now has a Templates | Interactive Messages tab strip) — the key here
  // must match the CURRENT nav label exactly, this map is keyed by label text.
  WhatsApp: 'whatsapp',
  Contacts: 'contacts',
  Appointments: 'appointments',
  'Instagram Comments': 'instagramComments',
  Campaigns: 'campaigns',
  Connections: 'integrations',
  Automation: 'automation',
  'Web Chats': 'leadForm',
  Billing: 'billing',
  'Active Plan': 'billing.activePlan',
  'Billing Amount': 'billing.activePlan',
  'Billing AI': 'billing.activePlan',
  'Usage History': 'billing.activePlan',
  Wallet: 'billing.activePlan',
  'User Management': 'userManagement',
  Settings: 'settings',
  'System Alerts': 'systemAlerts',
};

/**
 * Check if a module is enabled for a tenant
 */
function isModuleEnabled(modules: any, moduleKey: string): boolean {
  if (!modules) return false;

  const parts = moduleKey.split('.');
  if (parts.length === 1) {
    // Simple module (playground, actions, agents, etc.)
    return modules[moduleKey] === true;
  } else if (parts.length === 2) {
    // Nested module (activity.chatLogs, analytics.chats, etc.)
    const parent = parts[0];
    const child = parts[1];
    if (!parent || !child) return false;

    const parentModule = modules[parent];
    if (parentModule && typeof parentModule === 'object' && child in parentModule) {
      return (parentModule as Record<string, any>)[child] === true;
    }
    return false;
  }
  return false;
}

export const useModuleAccess = () => {
  const { user, tenant } = useAuth();
  const authState = useSelector((state: RootState) => state.auth);

  // Get modules from tenant (prefer from hook, fallback to Redux state)
  const modules = tenant?.modules || authState.tenant?.modules;

  /**
   * True if the user's RBAC-resolved permission set grants ANY access
   * (at least 'read') to this module — the bar for "should this module be
   * visible/reachable at all." Finer-grained view/edit/full distinctions
   * within a page that IS visible are usePermissionGate's job, not this
   * hook's — this only answers show-vs-hide.
   *
   * `effectivePermissions` is the real, server-resolved RBAC set (mirrors
   * backend shared/rbac/permission-resolution.ts's EffectivePermissions
   * exactly — see authSlice.ts). Falls back to the legacy role-string +
   * flat-permissions-array check ONLY when effectivePermissions hasn't been
   * populated yet (a session persisted to localStorage before this field
   * existed, before the next periodic /auth/me refresh backfills it) — never
   * once it's present, so a role edit an owner makes takes effect for real,
   * including narrowing what a tenant_admin can see, not just custom roles.
   */
  const hasRbacModuleAccess = (modulePath: string): boolean => {
    if (!user) return false;

    const effectivePermissions = user.effectivePermissions;
    if (effectivePermissions !== undefined) {
      return effectivePermissions === 'ALL_ACCESS' || modulePath in effectivePermissions;
    }

    // Transitional fallback — see doc comment above.
    if (user.role === 'tenant_owner' || user.role === 'tenant_admin') {
      if (!user.permissions || user.permissions.includes('*') || user.permissions.length === 0) {
        return true;
      }
    }
    return !!user.permissions && (user.permissions.includes('*') || user.permissions.includes(modulePath));
  };

  /**
   * Check if user has access to a specific module path
   */
  const hasModuleAccess = (modulePath: string): boolean => {
    if (!user) return false;

    // Superadmin has full access
    if (user.role === 'superadmin') {
      return true;
    }

    // Core modules always accessible. 'settings' joined 'dashboard' when its
    // registry key went hidden: the key is no longer grantable (no role's
    // effectivePermissions will ever contain it) and the backend enforces
    // nothing under it — the Settings page is a container whose sub-features
    // (WhatsApp templates, API integration, …) gate themselves.
    if (modulePath === 'dashboard' || modulePath === 'settings') {
      return true;
    }

    // If no modules configured at tenant level, deny access
    if (!modules) {
      return false;
    }

    // Tenant's PLAN must have the module enabled...
    if (!isModuleEnabled(modules, modulePath)) {
      return false;
    }

    // ...AND the user's ROLE must grant RBAC access to it. Both axes are
    // independent (a plan can include a module a role isn't granted, and
    // vice versa isn't possible since a role can't grant more than the plan
    // allows — see requirePermission's bundled check on the backend).
    return hasRbacModuleAccess(modulePath);
  };

  /**
   * Check if user has access to a route
   */
  const hasRouteAccess = (route: string): boolean => {
    // Drop the /admin prefix if present so that the lookup works for both URL structures
    const normalizedRoute = route.startsWith('/admin') ? route.replace('/admin', '') : route;
    const modulePath = routeToModuleMap[normalizedRoute];

    if (!modulePath) {
      // If route not in map, allow access (for backward compatibility)
      return true;
    }
    return hasModuleAccess(modulePath);
  };

  /**
   * Check if user has access to a menu item
   */
  const hasMenuItemAccess = (menuItemText: string): boolean => {
    const modulePath = menuItemToModuleMap[menuItemText];
    if (!modulePath) {
      // If menu item not in map, allow access (for backward compatibility)
      return true;
    }
    return hasModuleAccess(modulePath);
  };

  /**
   * Get all accessible modules
   */
  const getAccessibleModules = (): string[] => {
    if (!user || !modules) {
      return [];
    }

    const accessible: string[] = ['dashboard'];

    ALL_MODULE_KEYS.forEach((key) => {
      if (user.role === 'superadmin' || (isModuleEnabled(modules, key) && hasRbacModuleAccess(key))) {
        accessible.push(key);
      }
    });

    return Array.from(new Set(accessible));
  };

  /**
   * Get all restricted modules
   */
  const getRestrictedModules = (): string[] => {
    if (!user || !modules) {
      return [];
    }

    const restricted: string[] = [];

    ALL_MODULE_KEYS.forEach((key) => {
      const isAllowed =
        user.role === 'superadmin' || (isModuleEnabled(modules, key) && hasRbacModuleAccess(key));
      if (!isAllowed) {
        restricted.push(key);
      }
    });

    return restricted.filter((key) => key !== 'dashboard');
  };

  return {
    hasModuleAccess,
    hasRouteAccess,
    hasMenuItemAccess,
    getAccessibleModules,
    getRestrictedModules,
  };
};
